import React, { useEffect } from 'react'
import {
  StyleProperty,
  StyledContainer,
  StyledContinueButtonContainer,
  StyledPropertyLabel,
  StyledPropertyValue,
  StyledSummaryContainer,
} from '../../ProductCheckout/ProductCheckout.styled'
import indexIcon from 'assets/images/icons/SprengnetterAppsIndex.png'
import { useTranslation } from 'react-i18next'
import { selectAddressFormData, setAddressData } from 'slices/addressFormSlice'
import { useSelector } from 'react-redux'
import { IndexPeriodType } from 'types/IndexPeriodType'
import { periodTypeSelect } from '../IndexObjectDataForm/IndexObjectDataForm'
import checkMarkYes from 'assets/images/icons/checkMarkYes.svg'
import checkMarkNo from 'assets/images/icons/checkMarkNo.svg'
import Button from 'components/Button/Button'
import { useCreateAddressMutation, useUpdateAddressMutation } from 'services/address-api'
import { sanitizeAddressData } from '../../helper'
import { useAppDispatch } from 'store/hooks'
import { useToastEffect } from 'components/Toast/hooks/useToastEffect'

const IndexSummary = (props: any) => {
  const { setCurrentSubTab, setCurrentTab } = props
  const { t } = useTranslation()
  const addressFormData = useSelector(selectAddressFormData)
  const dispatch = useAppDispatch()

  const [
    createAddress,
    {
      isLoading: isCrateAddressLoading,
      isSuccess: isCrateAddressSuccess,
      isError: isCrateAddressError,
      data: createAddressData,
    },
  ] = useCreateAddressMutation()

  const [
    updateAddress,
    {
      isLoading: isUpdateAddressLoading,
      isSuccess: isUpdateAddressSuccess,
      isError: isUpdateAddressError,
      data: updateAddressData,
    },
  ] = useUpdateAddressMutation()

  const isLoading = isUpdateAddressLoading || isCrateAddressLoading

  const getIndexPeriodType = (periodType: IndexPeriodType) => {
    return t(periodTypeSelect.find((x) => x.key === periodType).value)
  }

  const getBooleanValue = (value?: boolean) => {
    return (
      <>
        {`${value}` === `true` ? (
          <img src={checkMarkYes} alt="checkMarkYes" />
        ) : `${value}` === `false` ? (
          <img src={checkMarkNo} alt="checkMarkNo" />
        ) : null}
        {`${value}` === `true` ? t('common.yes') : `${value}` === `false` ? t('common.no') : '—'}
      </>
    )
  }

  const onBackClick = () => {
    setCurrentTab((tab) => tab - 1)
  }

  const onContinueClick = () => {
    const data = sanitizeAddressData(addressFormData.addressData)
    if (addressFormData.isEdit) {
      updateAddress({
        id: addressFormData.addressData.id,
        payload: data,
      })
    } else {
      createAddress(data)
    }
  }

  useEffect(() => {
    if (isCrateAddressSuccess) {
      dispatch(setAddressData(createAddressData))
      setCurrentSubTab((tab) => tab + 1)
    }
  }, [isCrateAddressSuccess, createAddressData, setCurrentSubTab, dispatch])

  useEffect(() => {
    if (isUpdateAddressSuccess) {
      dispatch(setAddressData(updateAddressData))
      setCurrentSubTab((tab) => tab + 1)
    }
  }, [isUpdateAddressSuccess, updateAddressData, setCurrentSubTab, dispatch])

  useToastEffect(t('newAddressModal.addressTab.create-address.error.toast'), 'ERROR', [
    isCrateAddressError || isUpdateAddressError,
  ])

  return (
    <>
      <StyledContainer>
        <img src={indexIcon} alt="" className="app-icon" />
        <h1>{t('common.generateIndex')}</h1>
        <h2>{t('checkoutTab.summary')}</h2>
        <StyledSummaryContainer>
          <StyleProperty>
            <StyledPropertyLabel>{t('leadsList.addressFilter')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {`${addressFormData.addressData.street} ${addressFormData.addressData.houseNumber}`}
              <br />
              {`${addressFormData.addressData.town}, ${addressFormData.addressData.zip}`}
              <br />
              {`${addressFormData.addressData.nation}`}
            </StyledPropertyValue>
          </StyleProperty>
          <StyleProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.objectType')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {t(
                `objectType.${addressFormData.addressData.propertyDetails.objectType.toLowerCase()}`
              )}
            </StyledPropertyValue>
          </StyleProperty>
          <StyleProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.reportType')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {t(
                `newAddressModal.objectDataTab.reportType.${addressFormData.addressData.indexDetails.reportType.toLowerCase()}`
              )}
            </StyledPropertyValue>
          </StyleProperty>
          <StyleProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.periodType')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {getIndexPeriodType(addressFormData.addressData.indexDetails.periodType)}
            </StyledPropertyValue>
          </StyleProperty>
          <StyleProperty>
            <StyledPropertyLabel>
              {t('checkoutTab.summary.includeContactDetails')}
            </StyledPropertyLabel>
            <StyledPropertyValue>
              {getBooleanValue(addressFormData.addressData.indexDetails.includeContactDetails)}
            </StyledPropertyValue>
          </StyleProperty>
        </StyledSummaryContainer>
      </StyledContainer>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onBackClick()}>
          {t('common.backButton')}
        </Button>
        <Button
          kind="primary"
          loading={isLoading}
          disabled={isLoading}
          onClick={() => onContinueClick()}
        >
          {isLoading ? t('common.loadingButton') : t('common.saveAndProceedButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default IndexSummary
